import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  OPEN_COOKIE_SETTINGS_EVENT,
  onConsentChange,
  readConsent,
  writeConsent,
  globalPrivacyControlSet,
} from "@/lib/consent";
import { loadChatWidget, reloadWithoutEmbeds } from "@/lib/thirdParty";
import "./cookie-bar.css";

/**
 * The consent banner, and the only thing that starts the chat widget.
 *
 * Chat/calendar requests wait for a decision; the existing Google Preferred
 * Sources loader is separate and is described in the cookie policy.
 * "Reject" is a real rejection: it is stored, it is honoured
 * on the next visit, and the widget never loads. Accept and reject are the
 * same size, the same shape and the same weight, one click apart.
 */
export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  // How this opened decides whether it may take focus. The banner is a notice
  // that appears on its own on a first visit, so moving the caret into it
  // would interrupt a reader who never asked for it. Opened from the footer's
  // "Cookie settings" control it is the thing the visitor just asked for, and
  // leaving focus behind on that button means a keyboard or screen-reader
  // visitor has to hunt the whole page for what they opened.
  const [askedFor, setAskedFor] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  // A non-essential category starts off. The visitor turns it on, we never
  // pre-tick it for them.
  const [embeds, setEmbeds] = useState(false);
  const [inquirySource, setInquirySource] = useState(false);
  const sourceBlocked = globalPrivacyControlSet();

  useEffect(() => {
    const existing = readConsent();
    let timer: number | undefined;
    if (!existing && globalPrivacyControlSet()) {
      // A browser-level "do not sell or share" signal is already an answer.
      // Nothing loads without a stored yes, so honouring it means staying
      // quiet rather than asking again. The footer control still opens this
      // banner for anyone who wants to turn the tools on themselves.
    } else if (!existing) {
      // Delayed so the banner never covers the hero's product demo on first
      // paint. Nothing third-party is waiting on the delay: the page loads no
      // chat/calendar code until a choice exists.
      timer = window.setTimeout(() => setOpen(true), 1200);
    } else {
      setEmbeds(existing.embeds);
      setInquirySource(existing.inquirySource && !sourceBlocked);
    }

    const handler = () => {
      const current = readConsent();
      setEmbeds(current?.embeds ?? false);
      setInquirySource((current?.inquirySource ?? false) && !globalPrivacyControlSet());
      setShowPrefs(true);
      setAskedFor(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handler);
    };
  }, [sourceBlocked]);

  // The chat widget: consent first, then presence. The widget opens a proactive
  // bubble the moment it loads, so even a returning visitor who already said
  // yes gets it only once they have moved, touched something, or sat still for
  // six seconds.
  useEffect(() => {
    let allowed = readConsent()?.embeds === true;
    let wanted = false;
    const start = () => {
      if (allowed && wanted) loadChatWidget();
    };
    const want = () => {
      wanted = true;
      start();
    };
    const stopListening = onConsentChange((record) => {
      allowed = record.embeds;
      if (!record.embeds) return;
      // An explicit yes is itself an interaction, so nothing more is needed.
      wanted = true;
      start();
    });

    const events = ["scroll", "pointerdown", "keydown", "touchstart"] as const;
    for (const name of events) {
      window.addEventListener(name, want, { once: true, passive: true });
    }
    const quiet = window.setTimeout(want, 6000);
    return () => {
      stopListening();
      window.clearTimeout(quiet);
      for (const name of events) window.removeEventListener(name, want);
    };
  }, []);

  const panelRef = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  // Focus, only for the visitor who asked. This is a notice bar and not a
  // modal: it does not trap Tab and it does not lock scrolling, because doing
  // either to a first-time reader who has not asked for anything is worse
  // than the problem it would solve. What it does owe the visitor who opened
  // it from the footer is the caret, an Escape that closes without being read
  // as a decision, and their place back afterwards.
  useEffect(() => {
    if (!open || !askedFor) return;
    opener.current = document.activeElement;
    panelRef.current?.focus();

    const key = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      // Closing is not consenting. Nothing is written here, so the banner
      // comes back on the next load exactly as it would have.
      setAskedFor(false);
      setOpen(false);
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      const back = opener.current;
      if (back instanceof HTMLElement && document.contains(back)) back.focus();
    };
  }, [open, askedFor]);
  useEffect(() => {
    document.body.classList.toggle("cookie-consent-open", open);
    const root = document.documentElement;
    root.classList.toggle("cookie-banner-open", open);
    if (!open) {
      root.style.removeProperty("--cookie-banner-h");
      return () => {
        document.body.classList.remove("cookie-consent-open");
        root.classList.remove("cookie-banner-open");
      };
    }
    // Measured, never hard-coded: the hero stage ends exactly above the banner.
    const publish = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      root.style.setProperty(
        "--cookie-banner-h",
        `${Math.ceil(window.innerHeight - rect.top)}px`,
      );
    };
    publish();
    // The banner slides in, so re-read once the entrance settles.
    const settle = window.setTimeout(publish, 450);
    const observer = new ResizeObserver(publish);
    if (panelRef.current) observer.observe(panelRef.current);
    window.addEventListener("resize", publish);
    return () => {
      window.clearTimeout(settle);
      observer.disconnect();
      window.removeEventListener("resize", publish);
      document.body.classList.remove("cookie-consent-open");
      root.classList.remove("cookie-banner-open");
      root.style.removeProperty("--cookie-banner-h");
    };
  }, [open, showPrefs]);

  const persist = useCallback((value: boolean, source: boolean) => {
    const previouslyAllowed = readConsent()?.embeds === true;
    const record = writeConsent(value, source);
    setEmbeds(value);
    setInquirySource(record.inquirySource);
    setOpen(false);
    setShowPrefs(false);
    // The cleanup of the focus effect hands the caret back to whatever opened
    // this, so clearing the flag has to happen on the same close.
    setAskedFor(false);
    if (previouslyAllowed && !value && readConsent()?.embeds === false) {
      reloadWithoutEmbeds();
    }
  }, []);

  return (
    // `rebuild cc-shell` makes no box (display: contents). It is here so the
    // design system's tokens and .g-* component rules reach a banner that
    // App.tsx mounts outside the site's own wrapper.
    <div className="rebuild cc-shell">
      <AnimatePresence>
        {open && (
          // No ref on this element. framer-motion's PresenceChild reads
          // `children.props.ref`, and on React 18 the mere existence of a ref
          // on an AnimatePresence child arms the "`ref` is not a prop" warning
          // that fired on every page load. The panel below is what is measured
          // anyway, and it carries the ref instead.
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="cc-bar"
            // A region and not a dialog. role="dialog" tells a screen reader
            // to expect modal behaviour, focus moved in, Tab held inside,
            // Escape out, and this bar deliberately provides none of that on
            // the automatic first-visit path: it is a notice the page shows,
            // not a question that blocks the page. A labelled landmark is
            // what it actually is, and it stays reachable by name.
            role="region"
            aria-label="Cookie preferences"
          >
            <div
              className="cc-panel g-glass--quiet"
              ref={panelRef}
              tabIndex={-1}
            >
              <p className="cc-copy">
                Optional chat and booking tools use cookies. An optional,
                short-lived note helps us understand how you found us if you
                send an inquiry. Both wait for your choice.{" "}
                <a href="/cookie-policy" className="whitespace-nowrap">
                  Read the cookie policy
                </a>
                .
              </p>
              <div className="cc-actions">
                <button
                  type="button"
                  onClick={() => setShowPrefs((v) => !v)}
                  aria-expanded={showPrefs}
                  className="g-pill"
                >
                  Preferences
                </button>
                {/* Reject and accept are one click apart and carry the same
                    size, shape and weight. Do not shrink or soften this one. */}
                <button
                  type="button"
                  onClick={() => persist(false, false)}
                  className="g-btn g-btn--ghost g-btn--small"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => persist(true, true)}
                  className="g-btn g-btn--small"
                >
                  Accept all
                </button>
              </div>

              <AnimatePresence initial={false}>
                {showPrefs && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    className="cc-prefs"
                  >
                    <div className="cc-prefs-grid">
                      <PrefRow
                        title="Strictly necessary"
                        description="Remembers your privacy choice and a dismissed booking prompt. Always on."
                        checked
                        disabled
                      />
                      <PrefRow
                        title="Chat and booking tools"
                        description="Loads the chat widget and the booking calendar, which set their own cookies and browser storage. The cookie policy names every one of them."
                        checked={embeds}
                        onChange={setEmbeds}
                      />
                      <PrefRow
                        title="Inquiry source"
                        description={sourceBlocked
                          ? "Your browser's privacy signal keeps this off. Forms still work."
                          : "Remembers your entry page and available referral or campaign information in this tab for up to 30 minutes. Sent only if you submit a form."}
                        checked={inquirySource && !sourceBlocked}
                        disabled={sourceBlocked}
                        onChange={setInquirySource}
                      />
                    </div>
                    <div className="cc-prefs-foot">
                      <a href="/cookie-policy" className="cc-prefs-link">
                        What each one stores
                      </a>
                      <div className="cc-actions">
                        <button
                          type="button"
                          onClick={() => persist(false, false)}
                          className="g-btn g-btn--ghost g-btn--small"
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          onClick={() => persist(embeds, inquirySource)}
                          className="g-btn g-btn--small"
                        >
                          Save preferences
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PrefRow = ({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) => (
  <div className="cc-pref">
    <div className="min-w-0">
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={title}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className="cc-switch"
    >
      <span />
    </button>
  </div>
);
